/**
 * Charm Builder — Drag
 * GSAP Draggable setup. Loaded after gsap.min.js + Draggable.min.js (defer).
 * Exposes window.CharmBuilderDrag with attach / detach / detachAll, plus
 * isHintSeen / dismissHint for the show-once-ever "Drag to rearrange" hint.
 *
 * Each canvas overlay has TWO Draggable instances:
 *   1. Translation  — type: 'x,y' on the wrapper itself
 *   2. Rotation     — type: 'rotation' on the wrapper, triggered by the
 *                     `.charm-builder__canvas-charm-rotate` handle inside it.
 *
 * Coordinate model (translation):
 *   - State stores charm.x / charm.y as PERCENTAGES of the drop zone.
 *   - On render we set the element's CSS left/top in % and let GSAP own the
 *     centering + rotation transform via xPercent / yPercent / rotation.
 *   - Draggable type "x,y" adds an additive pixel offset on top.
 *   - On drag end we convert the pixel offset back to %, add to the
 *     pre-drag position (stored on data-pos-* attributes), and persist
 *     to state. setState re-renders, killing this Draggable and creating
 *     a fresh one at the new position.
 *
 * Rotation model:
 *   - State stores charm.rotation in DEGREES (0-360 normalised).
 *   - The wrapper's transform always reflects the persisted rotation.
 *   - On rotate end we read `this.rotation`, normalise, and persist.
 *
 * Base "open slots" swap:
 *   - While a charm is being moved or rotated, _showOpenBaseImage() adds
 *     .is-showing-open-slots to #cb-base-image, crossfading in the
 *     charm_builder_open_image overlay rendered by _renderCanvasBase (a
 *     no-op if that metafield isn't set on the current base variant).
 *   - _hideOpenBaseImage() reverses it on release, which GSAP fires
 *     unconditionally on pointer-up — covering both a real drop and a
 *     cancelled/aborted drag.
 */

window.CharmBuilderDrag = (() => {
  // Map<instanceId + ':' + unitIndex, { move: Draggable, rotate: Draggable }>
  const _instances = new Map();
  let _pluginRegistered = false;

  // Base "open slots" swap — shows the charm_builder_open_image overlay
  // (rendered by _renderCanvasBase in charm-builder.js, only present when the
  // base variant has that metafield) while a charm is actively being
  // dragged or rotated. Counted rather than a plain boolean so an overlapping
  // move+rotate press/release pair can't hide the overlay early.
  let _openDragCount = 0;

  function _showOpenBaseImage() {
    _openDragCount++;
    const baseEl = document.getElementById('cb-base-image');
    if (baseEl && baseEl.dataset.openImage) {
      baseEl.classList.add('is-showing-open-slots');
    }
  }

  function _hideOpenBaseImage() {
    _openDragCount = Math.max(0, _openDragCount - 1);
    if (_openDragCount === 0) {
      const baseEl = document.getElementById('cb-base-image');
      if (baseEl) baseEl.classList.remove('is-showing-open-slots');
    }
  }

  function _ready() {
    if (typeof gsap === 'undefined' || typeof Draggable === 'undefined') {
      return false;
    }
    if (!_pluginRegistered) {
      gsap.registerPlugin(Draggable);
      _pluginRegistered = true;
    }
    return true;
  }

  // "Drag to rearrange" hint (Figma 8214-56004) — shown once ever (localStorage) on
  // the first canvas charm, dismissed the first time any charm is dragged.
  const HINT_KEY = 'awe:cb:drag-hint-seen';
  let _hintSeenFallback = false; // localStorage can throw (private mode, blocked storage)

  function isHintSeen() {
    try {
      return _hintSeenFallback || localStorage.getItem(HINT_KEY) === '1';
    } catch (e) {
      return _hintSeenFallback;
    }
  }

  function dismissHint() {
    _hintSeenFallback = true;
    try {
      localStorage.setItem(HINT_KEY, '1');
    } catch (e) {}
    document.querySelectorAll('.charm-builder__canvas-charm-hint').forEach((hint) => {
      hint.closest('.charm-builder__canvas-charm')?.classList.remove('has-drag-hint');
      hint.remove();
    });
  }

  function _normaliseDegrees(deg) {
    let r = deg % 360;
    if (r < 0) r += 360;
    return r;
  }

  /**
   * Make an overlay draggable + rotatable.
   * The element's pre-drag percentage position must be set on
   * `data-pos-left` / `data-pos-top`; initial rotation on `data-rotation`.
   *
   * @param {HTMLElement} el
   * @param {string} instanceId
   */
  function attach(el, instanceId) {
    if (!_ready() || !el) return;

    const bounds = document.getElementById('cb-canvas-drop-zone');
    if (!bounds) return;

    const initialRotation = parseFloat(el.dataset.rotation) || 0;
    const rotor = el.querySelector('.charm-builder__canvas-charm-rotor');
    const handle = el.querySelector('.charm-builder__canvas-charm-rotate');

    // Outer wrapper: centered via xPercent/yPercent. Translation lives here.
    gsap.set(el, { xPercent: -50, yPercent: -50, x: 0, y: 0 });

    // Inner rotor: holds the visible image and the persisted rotation.
    // Putting rotation on a child element (rather than the outer wrapper)
    // avoids the GSAP single-Draggable-per-element conflict and keeps the
    // rotate handle visually upright while the charm spins.
    if (rotor) {
      gsap.set(rotor, { rotation: initialRotation, transformOrigin: '50% 50%' });
    }

    // ---- Translation instance (outer wrapper) ----
    // dragClickables:false makes GSAP skip drag-init when the press lands on
    // a button/input/select/textarea/anchor. The rotate and remove handles
    // are real <button> elements, so presses on them don't start translation.
    //
    // .is-dragging is added on onDragStart (NOT onPress) so a quick tap on
    // an overlay button isn't classified as a drag — otherwise the CSS rule
    // .is-dragging .button { pointer-events: none } would swallow the click.
    const [moveInstance] = Draggable.create(el, {
      type: 'x,y',
      bounds,
      inertia: false,
      dragClickables: false,
      onDragStart() {
        el.classList.add('is-dragging');
        _showOpenBaseImage();
        dismissHint();
      },
      onRelease() {
        el.classList.remove('is-dragging');
        _hideOpenBaseImage();
      },
      onDragEnd() {
        const rect = bounds.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const startLeftPct = parseFloat(el.dataset.posLeft) || 0;
        const startTopPct = parseFloat(el.dataset.posTop) || 0;

        const newLeftPct = startLeftPct + (this.x / rect.width) * 100;
        const newTopPct = startTopPct + (this.y / rect.height) * 100;

        const charms = CharmBuilder.state.charms.map((c) =>
          c.instanceId === instanceId ? { ...c, x: newLeftPct, y: newTopPct } : c
        );
        CharmBuilder.setState({ charms });
      },
    });

    // ---- Rotation instance (inner rotor, triggered by the handle) ----
    let rotateInstance = null;
    if (rotor && handle) {
      [rotateInstance] = Draggable.create(rotor, {
        type: 'rotation',
        trigger: handle,
        inertia: false,
        onPress() {
          el.classList.add('is-rotating');
          _showOpenBaseImage();
        },
        onRelease() {
          el.classList.remove('is-rotating');
          _hideOpenBaseImage();
        },
        onDragEnd() {
          const newRotation = _normaliseDegrees(this.rotation);
          const charms = CharmBuilder.state.charms.map((c) =>
            c.instanceId === instanceId ? { ...c, rotation: newRotation } : c
          );
          CharmBuilder.setState({ charms });
        },
      });
    }

    _instances.set(instanceId + ':' + (el.dataset.unitIndex || '0'), {
      move: moveInstance,
      rotate: rotateInstance,
    });
  }

  /**
   * Destroy both Draggable instances for a specific element key.
   * @param {string} key
   */
  function detach(key) {
    const pair = _instances.get(key);
    if (pair) {
      if (pair.move) pair.move.kill();
      if (pair.rotate) pair.rotate.kill();
      _instances.delete(key);
    }
  }

  /** Destroy all active Draggable instances. Called before each canvas rebuild. */
  function detachAll() {
    _instances.forEach((pair) => {
      if (pair.move) pair.move.kill();
      if (pair.rotate) pair.rotate.kill();
    });
    _instances.clear();
  }

  return { attach, detach, detachAll, isHintSeen, dismissHint };
})();
